import clsx from 'clsx'
import React, { useLayoutEffect, useMemo, useRef, useState, useEffect } from 'react'
import sizeMe from 'react-sizeme'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@mui/styles'
import moment from 'moment'
import Decimal from 'decimal.js'
import PerfectScrollbar from 'react-perfect-scrollbar'

import lang from '@/lang'
import config from '@/config'
import expenseConfig from '@/modules/expense/config'
import { DEBOUNCE_TIMES, DocumentControlButton } from '@/modules/base/components/buttons/index'
import DragIconSvg from '@assets/images/icons/drag.svg'
import ReloadIconSvg from '@assets/images/icons/reload.svg'
import ZoomMinusIconSvg from '@assets/images/icons/zoom_minus.svg'
import ZoomPlusIconSvg from '@assets/images/icons/zoom_plus.svg'
import IconGiniDocument from '@assets/images/icons/gini_document.svg'
import CloseIconSvg from '@assets/images/icons/cancel.svg'
import XmlDownloadIcon from '@assets/images/icons/v2/xml-download.svg'
import CloudUploadIcon from '@assets/images/icons/cloud_upload.svg'
import { OcrModal } from '@/modules/base/components/ocr/index'
import { PositionWithTaxCategory } from '@/modules/base/components/forms/positions-with-tax-category/index'
import { setAndValidateData } from '@/redux/ducks/receipt/edit'
import { resetState } from '@/redux/ducks/supplier/edit'
import checkValidReceiptNumberInput from '@/modules/receipt/_helpers/checkValidReceiptNumberInput'
import FileService from '@/modules/file/fileService'
import Loader from '@/modules/base/components/loader/loader'
import IconClose from '@assets/images/icons/v2/close.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconDownload from '@assets/images/icons/download.svg'
import documentPreviewImage from '@assets/images/documentPreview.png'

import { toast } from '@/modules/base/components/toast/toastConfig'

import { mimeTypes, receiptStates } from '@/modules/base/util/constants'
import { updateDocumentPreviewState } from '@/redux/ducks/document/preview'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import ThemeInterface from '@/theme-interface'
import ReceiptPreviewerItemImage from '@/modules/receipt/preview/item/receipt-previewer-item-image'
import ReceiptPreviewerItemPdf from '@/modules/receipt/preview/item/receipt-previewer-item-pdf'
// The „Dokument entfernen" button below, in its own module so the artifact preview can mount the
// identical control on an e-invoice slide, where this item is not rendered at all.
import ReceiptRemoveButton from '@/modules/receipt/preview/item/receipt-remove-button'
import thumbnailService from '@/modules/file/thumbnailService'
import ReceiptPreviewerItemXml from './receipt-previewer-item-xml'

const {
  MIMETYPE_JPG,
  MIMETYPE_JPEG,
  MIMETYPE_PNG,
  MIMETYPE_BMP,
  MIMETYPE_PDF,
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
  MIMETYPE_TEXT_XML,
  MIMETYPE_XML,
  MIMETYPE_CSV,
} = mimeTypes

const previewMimeTypes = [
  MIMETYPE_JPG,
  MIMETYPE_JPEG,
  MIMETYPE_PNG,
  MIMETYPE_BMP,
  MIMETYPE_PDF,
  MIMETYPE_TEXT_XML,
]
export const thumbnailMimeTypes = [
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_CSV,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
]

const xmlMimeTypes = [MIMETYPE_XML, MIMETYPE_TEXT_XML]

const GLOBAL_ROTATE_DEFAULT = 0
const GLOBAL_SCALE_DEFAULT = 1.0
const SCALE_MIN = 0.25
const {
  dateFormat: { api: apiDateFormat, client: clientDateFormat },
} = config
const { OPEN: STATE_OPEN } = receiptStates
const validDateFormats = [apiDateFormat, clientDateFormat, moment.ISO_8601]

const useStyles = makeStyles((theme: ThemeInterface) => ({
  actionContainer: {
    position: 'relative',
    zIndex: 1, // !Important: pdf-component
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: '1 1 0',
    width: '100%',
  },
  closeWrapper: {
    position: 'relative',
    marginRight: 30,
  },
  actionWrapper: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'center',
    marginLeft: 42,
  },
  close: {
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    opacity: 0.95,
    backgroundColor: theme.backgroundColor,
    borderTop: 0,
    borderBottomLeftRadius: '5px',
    borderBottomRightRadius: '5px',
    '& > *': {
      display: 'flex',
    },
  },
  mimeTypeComponent_loading: {
    paddingTop: '141.43%',
    background: `#fff url(${documentPreviewImage}) left top no-repeat`,
    marginBottom: 20,
    backgroundSize: 'contain',
    border: theme.border,
  },
  mimeTypeComponent: {
    width: '100%',
  },
  previewItem: {
    display: 'flex',
    justifyContent: ({ scale }: { scale: number }) => (scale < 1 ? 'center' : 'flex-start'),
    maxHeight: `calc(90vh - ${theme.pageContentOffset}px)`,
    outline: 'none',
    '&:hover $actionContainer': {
      display: 'flex',
    },
    height: '100%',
  },
  centerPreviewItem: {
    alignItems: 'center',
  },
  documentButton: {
    background: theme.white,
  },
  loader: {
    top: 'calc(45vh - 85px)',
  },
  downloadButton: {
    height: 45,
    maxHeight: 45,
    width: 45,
    maxWidth: 45,
    transition: '.2s',
    alignSelf: 'center',
    margin: 7.5,
  },
}))

interface NewDataObj {
  receiptNumber?: string,
  priceTotal?: number,
  outstandingAmount?: number,
  positions?: typeof PositionWithTaxCategory[],
  date?: string,
  ocrPayee?: string,
  ocrDataChanged?: number,
  filledData?: string[]
}

interface PerfectScrollbarInt extends PerfectScrollbar {
  _container: HTMLElement
  _ps: {
    isScrollbarXUsingBottom: boolean
    isScrollbarYUsingRight: boolean
    scrollbarXBottom: number
    scrollbarYRight: number
    scrollbarYRail: {
      style: {
        left: string
      }
    }
    scrollbarXRail: {
      style: {
        top: string
      }
    }
  }
}

interface ReceiptPreviewerItemProps {
  item: any,
  disableActions?: boolean,
  disableOcr?: boolean,
  disableDownload?: boolean,
  excludeFieldOptions?: string[],
  downloadUrl?: string,
  downloadLabel?: string,
  isNotDataLink?: boolean,
  size?: { width: number },
  key?: string,
  dateFor?: string,
  withSignatures?: boolean,
  hideRemove?: boolean,
  scale?: number,
  rotate?: number,
  date?: string,
  styles?: { previewItem: string },
  onStartAnalyze?: (id: string, ocrData: any) => void,
  onOcr?: (ocrData: any) => void,
  onRotate?: (degree: number) => void,
  onClose?: () => void,
  onDelete?: (item: any) => void,
  onUpdateReceipt?: (data: { degree: number, scale: number }) => void,
}

const ReceiptPreviewerItem = (props: ReceiptPreviewerItemProps) => {
  const {
    item,
    onDelete,
    disableActions = false,
    key,
    dateFor,
    onUpdateReceipt,
    onStartAnalyze,
    withSignatures,
    hideRemove,
    onOcr,
    onRotate,
    disableOcr,
    disableDownload,
    excludeFieldOptions = [],
    onClose,
    downloadUrl,
    downloadLabel,
    isNotDataLink,
    scale: propScale,
    rotate: propsRotate,
    styles,
  } = props

  const sizeMeWidth = props.size?.width

  const clonedItem = { ...item }
  const dispatch = useDispatch()
  const previewItemRef = useRef<HTMLDivElement>()
  const perfectScrollbarRef = useRef<PerfectScrollbarInt>()
  const { data } = useSelector((state) => state.receipt.edit)
  const { isLoading, isRebuilding, isDocumentReady } = useSelector(state => state.document.preview)

  const { metaData } = clonedItem
  const SCALE_DEFAULT = useMemo(() => (metaData && metaData.scale ? metaData.scale * 1 : GLOBAL_SCALE_DEFAULT), [isDocumentReady])
  const [scale, setScale] = useState(SCALE_DEFAULT)
  const ROTATE_DEFAULT = useMemo(
    () => (metaData && metaData.rotate ? metaData.rotate * 1 : GLOBAL_ROTATE_DEFAULT),
    [],
  )

  useWebSocketEvent(EventTypes.PDF_UPDATED, (msg) => {
    if (msg.type === EventTypes.PDF_UPDATED) {
      const [context, id] = clonedItem.id.split('-')
      if (context === msg.data.context && msg.data.id === id) {
        loadData()
      }
    }
  })

  const [rotate, setRotate] = useState<number>(ROTATE_DEFAULT)
  const [width, setWidth] = useState<number>()
  const [newItem, setNewItem] = useState({ ...clonedItem, url: '' })
  const [ocrModeEnabled, setOCRMode] = useState<boolean>(false)
  const [ocrResult, setOcrResult] = useState()
  const [ocrModalOpen, setOcrModalOpen] = useState<boolean>(false)
  const [finalDownLoadUrl, setFinalDownLoadUrl] = useState<string>(downloadUrl)
  const [excludeFromDatev, setExcludeFromDatev] = useState<boolean>(!!item.excludeFromDatev)

  useEffect(() => {
    setExcludeFromDatev(!!item.excludeFromDatev)
  }, [item.fileId, item.excludeFromDatev])

  const handleIncludeInDatev = () => {
    FileService.excludeFromDatev(item.fileId || item.id, false)
      .then(() => {
        setExcludeFromDatev(false)
        toast.showToast({ message: lang.documentUploadDrawer.includeInDatevSuccess })
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.documentUploadDrawer.excludeFromDatevError }))
  }

  const classes = useStyles({ ...props, scale })
  const memoizedSize = useMemo(() => sizeMeWidth, [sizeMeWidth, isDocumentReady])

  const { mimetype } = newItem

  const { id, state, positions, priceTotal, outstandingAmount } = data

  const hasNoPreview = !previewMimeTypes.includes(mimetype) && !clonedItem.minioId

  const calculateAndSetWidth = () => {
    const newWidth = memoizedSize || (previewItemRef.current ? previewItemRef.current.clientWidth : null)

    if (width === newWidth) return // Prevent re-render

    if (perfectScrollbarRef.current) {
      perfectScrollbarRef.current._ps.isScrollbarXUsingBottom = true
      perfectScrollbarRef.current._ps.isScrollbarYUsingRight = true
      perfectScrollbarRef.current._ps.scrollbarXBottom = 0
      perfectScrollbarRef.current._ps.scrollbarYRight = 0
      perfectScrollbarRef.current._ps.scrollbarYRail.style.left = ''
      perfectScrollbarRef.current._ps.scrollbarXRail.style.top = ''
    }

    setWidth(newWidth)
  }

  const resetScrollPosition = () => {
    const container = perfectScrollbarRef.current?._container

    if (container) {
      container.scrollTop = 0
      container.scrollLeft = 0
    }
  }

  const showFile = previewMimeTypes.includes(clonedItem?.mimetype)
  const showThumbnail = thumbnailMimeTypes.includes(clonedItem?.mimetype) && clonedItem.minioId

  const setLoaders = (isLoading, isRebuilding = undefined) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const loaders: any = {}
    if (isLoading !== undefined) {
      loaders.isLoading = isLoading
    }
    if (isRebuilding !== undefined) {
      loaders.isRebuilding = isRebuilding
    }
    dispatch(updateDocumentPreviewState({ isLoading, isRebuilding }))
  }

  const loadData = () => {
    setLoaders(true)
    const isPreview = clonedItem.url?.includes('/preview')
    if (isPreview) {
      return FileService.fetchPreviewUrl(clonedItem.url)
        .then(({ url, shallRebuild }) => {
          if (shallRebuild) {
            setLoaders(shallRebuild, shallRebuild)
          }

          const [_, possibleParameters] = finalDownLoadUrl.split('?')
          const params = possibleParameters.split('&').filter(param => !param.startsWith('token='))
          const newDownLoadUrl = url + (params.length ? `?${params.join('&')}` : '')
          setFinalDownLoadUrl(newDownLoadUrl)
          FileService.fetchFileDataByUrl(newDownLoadUrl)
            .then((data) => {
              setNewItem({ ...clonedItem, url: data })
            })
            .finally(() => setLoaders(false, false))
        })
    } else {
      if (showFile) {
        if (clonedItem?.url) {
          FileService.fetchFileDataByUrl(clonedItem.url)
            .then((data) => {
              setNewItem({ ...clonedItem, url: data })
            })
            .finally(() => setLoaders(false, false))
        }
      } else if (showThumbnail) {
        thumbnailService.fetch(clonedItem.minioId, 1000).then(({ url, state }) => {
          if (state === 'ready' || state === 'creating') {
            setNewItem({ ...clonedItem, url })
            setLoaders(false, false)
          }
        })
      } else {
        setNewItem(clonedItem)
        setLoaders(false, false)
      }
    }
  }

  useEffect(() => {
    setOCRMode(false)
    loadData()
  }, [item.url])

  useEffect(() => {
    const externalScale = propScale
    if (externalScale > 0) {
      setScale(externalScale)
    }
  }, [propScale])

  useEffect(() => {
    const externalRotate = propsRotate
    setRotate(externalRotate || ROTATE_DEFAULT)
  }, [propsRotate])

  useLayoutEffect(() => {
    calculateAndSetWidth()
  }, [previewItemRef.current, memoizedSize])

  const handleModalFinish = (ocrData) => {
    if (onOcr) {
      handleModalClose()
      return onOcr(ocrData)
    }
    const { text, field } = ocrData
    const trimmedText = text.trim()

    if (!trimmedText) return toast.showToast({ type: 'error', message: lang.expenseEmptyOcrValue })

    let newData: NewDataObj = {}
    switch (field) {
      case 'receiptNumber':
        if (!checkValidReceiptNumberInput(trimmedText)) {
          return toast.showToast({
            type: 'error',
            message: lang.expenseReceiptNumberInvalidCharacterErrorMessage,
          })
        }
        newData = { receiptNumber: trimmedText }
        break
      case 'priceTotal':
        const parsedValue = new Decimal(parseFloat(trimmedText.replace(',', '.'))).abs().toDP(2).toNumber()

        if (isNaN(parsedValue)) { return toast.showToast({ type: 'error', message: lang.expenseOcrWrongPriceFormat }) }

        newData = {
          priceTotal: new Decimal(priceTotal).eq(0)
            ? parsedValue
            : state !== STATE_OPEN
              ? priceTotal
              : new Decimal(priceTotal).plus(parsedValue).toDP(2).toNumber(),
          outstandingAmount: new Decimal(outstandingAmount).eq(0)
            ? parsedValue
            : state !== STATE_OPEN
              ? outstandingAmount
              : new Decimal(parsedValue).plus(outstandingAmount).toDP(2).toNumber(),
          positions: new Decimal(priceTotal).eq(0)
            ? [{ ...positions[0], totalGross: parsedValue }]
            : [...positions, PositionWithTaxCategory({ totalGross: parsedValue, dateFor })],
        }
        break
      case 'date':
        const parsedDate = moment(trimmedText, validDateFormats, true)
        if (!parsedDate.isValid()) {
          return toast.showToast({ type: 'error', message: lang.expenseOcrWrongDateFormat })
        }

        newData = { date: parsedDate.format(apiDateFormat) }
        break
      case 'payee':
      case 'sender':
        dispatch(resetState())
        newData = { ocrPayee: trimmedText, ocrDataChanged: new Date().getTime() }
        break
      default:
        newData = { [field]: trimmedText }
    }

    newData.filledData = [field]

    dispatch(setAndValidateData({ ...data, ...newData }))
    handleModalClose()
  }
  const handleOcrResult = (text) => {
    setOcrResult(text)
    setOcrModalOpen(true)
  }
  const handleModalClose = () => {
    setOcrModalOpen(false)
  }

  const mimeTypeComponent = useMemo(() => {
    switch (mimetype) {
      case mimeTypes.MIMETYPE_PDF: {
        return (
          <>
            <ReceiptPreviewerItemPdf
              receiptUrl={newItem.url}
              withSignatures={withSignatures}
              scale={scale}
              rotate={rotate}
              width={width}
              ocrModeEnabled={ocrModeEnabled}
              onOcrResult={handleOcrResult}
              onPageLoadSuccess={() => calculateAndSetWidth()}
            />
          </>
        )
      }
      case mimeTypes.MIMETYPE_XML:
      case mimeTypes.MIMETYPE_TEXT_XML: {
        return (
          <ReceiptPreviewerItemXml
            receiptUrl={newItem.url}
            name={newItem.name}
          />
        )
      }
      default: {
        return (
          <ReceiptPreviewerItemImage
            receipt={newItem}
            scale={scale}
            rotate={rotate}
            width={width}
            onOcrResult={handleOcrResult}
            ocrModeEnabled={ocrModeEnabled}
            hasNoPreview={hasNoPreview}
            showPreviewBanner={showThumbnail}
          />
        )
      }
    }
  }, [newItem, scale, rotate, width, ocrModeEnabled, isLoading])

  const rotateRight = () => {
    const degree = rotate >= 270 ? 0 : rotate + 90
    setRotate(degree)
    onRotate?.(degree)
    onUpdateReceipt?.({ ...clonedItem, rotate: degree, scale })
    calculateAndSetWidth()
  }

  const persistScale = (scale) => {
    setScale(scale)
    onUpdateReceipt?.({ ...clonedItem, scale, rotate })
  }

  const zoom = (zoomStep) => {
    const newScale = Math.max(scale + zoomStep, SCALE_MIN)
    return newScale
  }

  const getControlButtons = (mimetype) => {
    if (ocrModeEnabled) {
      return <DocumentControlButton title={lang.expense.previewToolbar.items.ocrStart} icon={CloseIconSvg} onClick={() => setOCRMode(false)} />
    }
    return (
      <>
        <div className={clsx({ [classes.actionWrapper]: onClose })}>
          {!xmlMimeTypes.includes(mimetype) && (
            <>
              <DocumentControlButton
                title={lang.expense.previewToolbar.items.zoomOut}
                icon={ZoomMinusIconSvg}
                customClass={classes.documentButton}
                onClick={() => {
                  persistScale(zoom(-0.1))
                }}
                disabled={!isDocumentReady}
                debounceTime={DEBOUNCE_TIMES.OFF}
              />
              <DocumentControlButton
                title={lang.expense.previewToolbar.items.zoomIn}
                icon={ZoomPlusIconSvg}
                customClass={classes.documentButton}
                onClick={() => {
                  persistScale(zoom(0.1))
                }}
                disabled={!isDocumentReady}
                debounceTime={DEBOUNCE_TIMES.OFF}
              />
              <DocumentControlButton
                title={lang.expense.previewToolbar.items.rotate90}
                icon={ReloadIconSvg}
                customClass={classes.documentButton}
                onClick={rotateRight}
                disabled={!isDocumentReady}
                debounceTime={DEBOUNCE_TIMES.OFF}
              />
              <DocumentControlButton
                title={lang.expense.previewToolbar.items.reset}
                icon={DragIconSvg}
                customClass={classes.documentButton}
                onClick={() => {
                  persistScale(SCALE_DEFAULT)
                  setRotate(ROTATE_DEFAULT)
                  resetScrollPosition()
                }}
                disabled={!isDocumentReady}
                debounceTime={DEBOUNCE_TIMES.OFF}
              />
            </>
          )}
          {!disableDownload && isNotDataLink && (
            <DocumentControlButton
              customClass={clsx([classes.downloadButton, classes.documentButton])}
              icon={xmlMimeTypes.includes(mimetype) ? XmlDownloadIcon : IconDownload}
              title={xmlMimeTypes.includes(mimetype) ? lang.receipt.preview.downloadXML : downloadLabel}
              disabled={!isDocumentReady}
              onClick={() => {
                window.open(finalDownLoadUrl, '_blank')
              }} />
          )}
          {excludeFromDatev && (
            <DocumentControlButton
              customClass={classes.documentButton}
              icon={CloudUploadIcon}
              title={lang.documentUploadDrawer.includeInDatevButton}
              onClick={handleIncludeInDatev} />
          )}
        </div>
        {!disableOcr && onStartAnalyze && (
          <DocumentControlButton
            title={lang.expense.previewToolbar.items.analyzeStart}
            icon={IconGiniDocument}
            customClass={classes.documentButton}
            onClick={() => onStartAnalyze(item.fileId || item.id, item.metaData?.ocr)}
          />
        )}
        {!!onClose && <div className={classes.closeWrapper} ><SVGIcon onClick={onClose} className={classes.close} svg={IconClose} width="12px" height="12px" /></div>}
      </>
    )
  }

  const pdfActions = getControlButtons(mimetype)

  const filteredOptions = expenseConfig.ocrFieldSelectOptions.filter(({ value }) => !excludeFieldOptions.includes(value))
  const ocrFieldOptions
    = id && state !== STATE_OPEN
      ? filteredOptions.filter((option) => !option.hiddenIfPaid)
      : filteredOptions

  // The button itself moved to `receipt-remove-button.tsx` (verbatim) so the artifact preview can
  // render the identical control on an e-invoice slide, where this item is not mounted at all.
  const renderUnlinkOrDelete = ({ item, onDelete }) => (
    <ReceiptRemoveButton item={item} hideRemove={hideRemove} onDelete={onDelete} />
  )

  const withActionsEnabled = (
    <>
      <div className={classes.actionContainer}>
        <div className={classes.actions}>
          {pdfActions}
          {!ocrModeEnabled ? renderUnlinkOrDelete({ item, onDelete }) : null}
        </div>
      </div>
      <PerfectScrollbar key={key} component="div" ref={perfectScrollbarRef}>
        <div
          className={clsx(classes.previewItem, hasNoPreview && classes.centerPreviewItem, styles?.previewItem && styles?.previewItem)}
          ref={previewItemRef}
        >
          <div className={clsx(classes.mimeTypeComponent, isLoading && classes.mimeTypeComponent_loading)}>
            {isRebuilding && <Loader text={lang.documentCreating} className={classes.loader} />}
            {isLoading && !isRebuilding && <Loader text={lang.documents.fileLoaderText} customClass={classes.loader} />}
            {!isLoading && mimeTypeComponent}
          </div>
          <OcrModal
            open={ocrModalOpen}
            text={ocrResult}
            selectOptions={ocrFieldOptions}
            onFinish={handleModalFinish}
            onClose={handleModalClose}
          />
        </div>
      </PerfectScrollbar>
    </>
  )
  const withActionsDisabled = (
    <div className={clsx(classes.previewItem, hasNoPreview && classes.centerPreviewItem)} ref={previewItemRef}>
      <div className={clsx(classes.mimeTypeComponent, isLoading && classes.mimeTypeComponent_loading)}>
        {isRebuilding && <Loader text={lang.documentCreating} className={classes.loader} />}
        {isLoading && !isRebuilding && <Loader text={lang.documents.fileLoaderText} customClass={classes.loader} />}
        {!isLoading && mimeTypeComponent}
      </div>
    </div>
  )

  return (
    <>
      {disableActions && withActionsDisabled}
      {!disableActions && withActionsEnabled}
    </>
  )
}

export default sizeMe({ noPlaceholder: true })(ReceiptPreviewerItem)
